import React, { useState } from 'react';
import { Search, Shield } from 'lucide-react';
import { getGradingCheck, searchCards } from '../../api/dashboard';

export default function GradingChecker() {
  const [query, setQuery] = useState('');
  const [data, setData] = useState<any>(null);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (q?: string) => {
    const val = q || query;
    if (!val.trim()) return;
    setLoading(true);
    setSuggestions([]);
    try {
      if (val.includes('-')) {
        const d = await getGradingCheck(val.trim());
        setData(d);
      } else {
        const results = await searchCards(val);
        if (results?.direct?.card) {
          setData(results.direct);
        } else if (results?.cards?.length === 1) {
          const d = await getGradingCheck(results.cards[0].id);
          setData(d);
        } else {
          setSuggestions(results?.cards || []);
          setData(null);
        }
      }
    } catch { }
    setLoading(false);
  };

  const selectCard = async (id: string) => {
    setQuery(id);
    setSuggestions([]);
    setLoading(true);
    try {
      const d = await getGradingCheck(id);
      setData(d);
    } catch { }
    setLoading(false);
  };

  const pop = data?.population;
  const roi = data?.roi;
  const graded = data?.prices?.graded;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Shield size={24} className="text-purple-400" /> Grading Checker
        </h2>
        <p className="text-sm text-neutral-500 mt-1">Analyse ROI de grading PSA / CGC / BGS</p>
      </div>

      {/* Search */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-3 text-neutral-500" />
          <input type="text" value={query} onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Card ID ou nom..."
            className="w-full pl-10 pr-4 py-2.5 bg-surface border border-border rounded-xl text-white placeholder:text-neutral-600 focus:outline-none focus:border-purple-400" />
        </div>
        <button onClick={() => handleSearch()} className="px-6 py-2.5 bg-purple-500 rounded-xl text-white font-medium hover:bg-purple-600">
          Analyser
        </button>
      </div>

      {suggestions.length > 0 && (
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          {suggestions.slice(0, 10).map(s => (
            <div key={s.id} onClick={() => selectCard(s.id)}
              className="px-4 py-3 hover:bg-neutral-800 cursor-pointer border-b border-border/50 flex justify-between">
              <span className="text-white">{s.name_fr || s.name}</span>
              <span className="text-neutral-500 text-sm">{s.id}</span>
            </div>
          ))}
        </div>
      )}

      {loading && <div className="text-center py-10 text-neutral-500">Analyse en cours...</div>}

      {data?.card && (
        <div className="space-y-6">
          {/* Verdict */}
          {roi && roi.verdict !== 'NO_DATA' && (
            <div className={`rounded-2xl p-6 text-center border-2 ${
              roi.verdict === 'GRADE_IT' ? 'bg-green-500/5 border-green-500/50' :
              roi.verdict === 'RISKY' ? 'bg-yellow-500/5 border-yellow-500/50' :
              'bg-red-500/5 border-red-500/50'
            }`}>
              <div className={`text-3xl font-bold ${
                roi.verdict === 'GRADE_IT' ? 'text-green-400' :
                roi.verdict === 'RISKY' ? 'text-yellow-400' : 'text-red-400'
              }`}>
                {roi.verdict === 'GRADE_IT' ? 'GRADE IT!' : roi.verdict === 'RISKY' ? 'RISKY' : 'NOT WORTH IT'}
              </div>
              <div className="text-neutral-400 mt-1">
                Expected value: {roi.expected_value != null ? `${roi.expected_value > 0 ? '+' : ''}$${roi.expected_value.toFixed(2)}` : '—'}
              </div>
            </div>
          )}

          {/* Population stats */}
          {pop && (
            <div className="grid grid-cols-4 gap-4">
              {pop.total > 0 && (
                <div className="bg-surface border border-border rounded-2xl p-4 text-center">
                  <div className="text-xs text-neutral-500 uppercase">PSA Total</div>
                  <div className="text-2xl font-bold text-accent">{pop.total.toLocaleString()}</div>
                  {pop.gem_rate != null && <div className="text-xs text-neutral-500">Gem rate: {pop.gem_rate}%</div>}
                </div>
              )}
              {pop.total_pca > 0 && (
                <div className="bg-surface border border-border rounded-2xl p-4 text-center">
                  <div className="text-xs text-neutral-500 uppercase">PCA Total</div>
                  <div className="text-2xl font-bold text-yellow-400">{pop.total_pca.toLocaleString()}</div>
                  {pop.gem_rate_pca != null && <div className="text-xs text-neutral-500">Gem rate: {pop.gem_rate_pca}%</div>}
                </div>
              )}
              {pop.total_ccc > 0 && (
                <div className="bg-surface border border-border rounded-2xl p-4 text-center">
                  <div className="text-xs text-neutral-500 uppercase">CCC Total</div>
                  <div className="text-2xl font-bold text-purple-400">{pop.total_ccc.toLocaleString()}</div>
                  {pop.gem_rate_ccc != null && <div className="text-xs text-neutral-500">Gem rate: {pop.gem_rate_ccc}%</div>}
                </div>
              )}
              {pop.most_likely_grade && (
                <div className="bg-surface border border-border rounded-2xl p-4 text-center">
                  <div className="text-xs text-neutral-500 uppercase">Grade probable</div>
                  <div className="text-2xl font-bold text-purple-300">PSA {pop.most_likely_grade.grade}</div>
                  <div className="text-xs text-neutral-500">{pop.most_likely_grade.percentage}%</div>
                </div>
              )}
            </div>
          )}

          {/* Graded prices comparison */}
          {graded && (
            <div className="grid grid-cols-3 gap-4">
              {['psa', 'cgc', 'bgs'].map(company => {
                const grades = graded[company] || {};
                const gradeKeys = Object.keys(grades).sort((a, b) => Number(b) - Number(a));
                if (gradeKeys.length === 0) return null;
                const colors: Record<string, string> = { psa: 'border-red-500', cgc: 'border-green-500', bgs: 'border-purple-500' };
                return (
                  <div key={company} className="bg-surface border border-border rounded-2xl overflow-hidden">
                    <div className={`px-4 py-2 border-t-2 ${colors[company]} font-bold text-white text-sm uppercase`}>
                      {company.toUpperCase()} <span className="text-neutral-500 font-normal">{gradeKeys.length} grades</span>
                    </div>
                    <div className="divide-y divide-border/50">
                      {gradeKeys.map(g => (
                        <div key={g} className="px-4 py-2 flex justify-between text-sm">
                          <span className="text-white font-medium">{company.toUpperCase()} {g}</span>
                          <span className="text-green-400 font-semibold">${grades[g].price_usd?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Recent sales */}
          {data.recent_sales?.length > 0 && (
            <div>
              <h3 className="text-sm text-neutral-500 uppercase tracking-wider mb-3">
                Ventes recentes ({data.recent_sales.length})
              </h3>
              <div className="bg-surface border border-border rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-4 py-2 text-xs text-neutral-500">Date</th>
                      <th className="text-left px-4 py-2 text-xs text-neutral-500">Grade</th>
                      <th className="text-right px-4 py-2 text-xs text-neutral-500">Prix</th>
                      <th className="text-left px-4 py-2 text-xs text-neutral-500">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_sales.slice(0, 15).map((s: any, i: number) => (
                      <tr key={i} className="border-b border-border/30">
                        <td className="px-4 py-2 text-sm text-neutral-400">{s.date?.slice(0, 10)}</td>
                        <td className="px-4 py-2 text-sm font-medium text-white">{s.grade || s.condition || '—'}</td>
                        <td className="px-4 py-2 text-sm text-green-400 text-right font-semibold">${s.price?.toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-neutral-500">{s.source || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
